import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Check, X, Warehouse, Store, Trash2, ClipboardCheck } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button } from "../lib/ui";
import { useToast } from "../lib/toast";
import { fmtNum, fmtUnit, fmtRp } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";

const STATUS_TONE = { PENDING: "warn", APPROVED: "success", REJECTED: "danger" };

export default function Approvals() {
  const toast = useToast();
  const { isRole } = useAuth();
  const { t } = useT();
  const [tab, setTab] = useState("adjustments");
  const [data, setData] = useState(null);
  const [waste, setWaste] = useState(null);
  const [filter, setFilter] = useState("PENDING");
  const [busy, setBusy] = useState(null);

  const canApprove = isRole("SUPER_ADMIN", "CENTRAL_PURCHASING", "WAREHOUSE");
  const canApproveWaste = isRole("SUPER_ADMIN", "CENTRAL_PURCHASING");

  const load = useCallback(() => {
    const qs = filter === "ALL" ? "" : `?status=${filter.toLowerCase()}`;
    api.get(`/adjustment-requests${qs}`).then(({ data }) => setData(data));
    if (canApproveWaste) api.get("/waste/pending").then(({ data }) => setWaste(data)).catch(() => setWaste({ waste: [], pending_count: 0 }));
  }, [filter, canApproveWaste]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/adjustment-requests/${id}/${action}`);
      toast.success(action === "approve" ? t("Adjustment approved & applied") : t("Request rejected"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(null); }
  };

  const actWaste = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/waste/${id}/${action}`);
      toast.success(action === "approve" ? t("Waste approved & stock reduced") : t("Waste rejected"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(null); }
  };

  if (!data) return <PageLoader />;

  const wastePending = waste?.pending_count || 0;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2">
      <PageHeader title={t("Approvals")} subtitle={t("Stock-take variance (>5%) & high-value waste need HQ sign-off")} icon={ShieldCheck} />

      {/* Tabs */}
      <div className="mb-4 flex flex-wrap gap-2" data-testid="approvals-tabs">
        <button onClick={() => setTab("adjustments")} data-testid="approvals-tab-adjustments"
          className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-semibold transition-all ${tab === "adjustments" ? "border-primary bg-primary text-white" : "border-line bg-white text-ink-soft hover:bg-stone-50"}`}>
          <ClipboardCheck size={15} /> {t("Stock Adjustments")} {data.pending_count > 0 && <span className={`rounded-full px-1.5 text-[11px] ${tab === "adjustments" ? "bg-white/25" : "bg-warn-light text-warn-text"}`}>{data.pending_count}</span>}
        </button>
        {canApproveWaste && (
          <button onClick={() => setTab("waste")} data-testid="approvals-tab-waste"
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-semibold transition-all ${tab === "waste" ? "border-primary bg-primary text-white" : "border-line bg-white text-ink-soft hover:bg-stone-50"}`}>
            <Trash2 size={15} /> {t("Waste")} {wastePending > 0 && <span className={`rounded-full px-1.5 text-[11px] ${tab === "waste" ? "bg-white/25" : "bg-warn-light text-warn-text"}`}>{wastePending}</span>}
          </button>
        )}
      </div>

      {tab === "adjustments" && (
        <>
          <div className="mb-4 flex flex-wrap gap-2">
            {["PENDING", "APPROVED", "REJECTED", "ALL"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`approvals-filter-${f}`}
                className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all ${filter === f ? "bg-ink text-white" : "bg-white border border-line text-ink-soft hover:bg-stone-50"}`}>
                {f === "ALL" ? t("All") : t(f)}
              </button>
            ))}
          </div>

          {data.requests.length === 0 ? (
            <Card className="p-10 text-center text-ink-mute" data-testid="approvals-empty">{t("No adjustment requests.")}</Card>
          ) : (
            <Card className="overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
                    <th className="px-4 py-3">{t("Product")}</th><th className="px-4 py-3">{t("Source")}</th>
                    <th className="px-4 py-3 text-right">{t("Current → Requested")}</th><th className="px-4 py-3 text-right">{t("Difference")}</th>
                    <th className="px-4 py-3">{t("Reason")}</th><th className="px-4 py-3">{t("Requested By")}</th>
                    <th className="px-4 py-3 text-center">{t("Status")}</th>{canApprove && <th className="px-4 py-3 text-right">{t("Action")}</th>}</tr></thead>
                  <tbody>
                    {data.requests.map((r) => (
                      <tr key={r.id} className="border-b border-line/60 hover:bg-stone-50/60" data-testid={`approval-row-${r.id}`}>
                        <td className="px-4 py-3 font-semibold">{r.product_name}<div className="text-[11px] font-normal text-ink-mute"><span className="inline-flex items-center gap-1">{r.scope === "central" ? <Warehouse size={11} /> : <Store size={11} />}{r.scope === "central" ? t("Central") : t("Branch")}</span></div></td>
                        <td className="px-4 py-3"><Badge tone={r.source === "opname" ? "info" : "mute"}>{r.source === "opname" ? t("Stock Take") : t("Manual")}</Badge></td>
                        <td className="px-4 py-3 text-right text-ink-soft">{fmtNum(r.current_quantity)} → <span className="font-semibold text-ink">{fmtNum(r.requested_quantity)}</span> {fmtUnit(r.unit)}</td>
                        <td className={`px-4 py-3 text-right font-bold ${r.difference < 0 ? "text-danger-text" : "text-success-text"}`}>{r.difference > 0 ? "+" : ""}{fmtNum(r.difference)} {r.variance_pct != null && <span className="text-xs">({r.variance_pct > 0 ? "+" : ""}{r.variance_pct}%)</span>}</td>
                        <td className="px-4 py-3 text-xs text-ink-soft">{r.reason}</td>
                        <td className="px-4 py-3 text-xs text-ink-soft">{r.requested_by_name}<br /><span className="text-ink-mute">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span></td>
                        <td className="px-4 py-3 text-center"><Badge tone={STATUS_TONE[r.status] || "mute"}>{t(r.status)}</Badge>
                          {r.status !== "PENDING" && r.approved_by_name && <div className="mt-1 text-[10px] text-ink-mute">{t("by")} {r.approved_by_name}</div>}</td>
                        {canApprove && (
                          <td className="px-4 py-3">
                            {r.status === "PENDING" ? (
                              <div className="flex justify-end gap-1.5">
                                <Button size="sm" variant="success" loading={busy === r.id} onClick={() => act(r.id, "approve")} data-testid={`approve-${r.id}`}><Check size={14} /> {t("Approve")}</Button>
                                <Button size="sm" variant="danger" onClick={() => act(r.id, "reject")} data-testid={`reject-${r.id}`}><X size={14} /></Button>
                              </div>
                            ) : <span className="text-xs text-ink-mute">—</span>}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {tab === "waste" && canApproveWaste && (
        (waste?.waste || []).length === 0 ? (
          <Card className="p-10 text-center text-ink-mute" data-testid="waste-approvals-empty">{t("No waste reports awaiting approval.")}</Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="waste-approvals-list">
            {waste.waste.map((w) => (
              <Card key={w.id} className="p-4" data-testid={`waste-approval-${w.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-heading font-bold text-ink">{w.product_name}</p>
                    <p className="text-xs text-ink-mute">{w.branch_name || t("Central")} · {t("by")} {w.created_by_name}</p>
                  </div>
                  <Badge tone="warn">{t("Pending")}</Badge>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-sm font-bold text-danger-text">{fmtNum(w.quantity)} {fmtUnit(w.unit)} · {fmtRp(w.waste_value)}</span>
                  {w.photo && <img src={w.photo} alt="" className="h-12 w-12 rounded-lg object-cover" />}
                </div>
                <p className="mt-2 text-xs text-ink-soft">{w.reason}</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {w.over_value && <Badge tone="danger">{t("> Rp 100k")}</Badge>}
                  {w.over_pct && <Badge tone="danger">{t("> 10% stock")} ({w.pct_of_stock}%)</Badge>}
                </div>
                <div className="mt-3 flex gap-2 border-t border-line pt-3">
                  <Button size="sm" variant="success" loading={busy === w.id} onClick={() => actWaste(w.id, "approve")} data-testid={`waste-approve-${w.id}`}><Check size={14} /> {t("Approve")}</Button>
                  <Button size="sm" variant="danger" onClick={() => actWaste(w.id, "reject")} data-testid={`waste-reject-${w.id}`}><X size={14} /> {t("Reject")}</Button>
                </div>
              </Card>
            ))}
          </div>
        )
      )}
    </div>
  );
}
